import { useMemo, useState } from "react";
import { useWebClientApi } from "src/api";
import {
  fromBorrowerPersonalInfo,
  toDependentsUpdateReq,
  type PersonalInfoFormData,
} from "src/api/adapters";
import {
  useResponseRedirect,
  useRoleForBorrower,
  useSectionProgress,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import DependentsModal from "./DependentsModal";
import DependentsPageTitle from "./DependentsPageTitle";
import {
  FormTable,
  FormTableBody,
  MaritalStatusFields,
  NearestRelativeFields,
  UserGroup02Icon,
  UserIcon,
} from "@porchpass/ui";
import {
  fromDependentsFormData,
  parseDependentsAgesString,
  toMaritalStatusFormData,
} from "src/forms/adapters/maritalStatus";
import {
  formatNearestRelativeForSubmit,
  fromNearestRelativeFormData,
  toNearestRelativeFormData,
} from "src/forms/adapters/nearestRelative";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import {
  commaSeparatedDigits,
  validAges,
  lessThanMaxLength,
  isValidPhoneNumber,
  stripCountryCode,
  test,
  validate,
  type ValidationMapperFunc,
} from "src/utils";
import type { DtoBorrower } from "@porchpass/api-client";

const getDynamicValidationMapper =
  (hasDependentsInput: boolean): ValidationMapperFunc<PersonalInfoFormData> =>
  (formData) => ({
    dependents_ages_years: hasDependentsInput
      ? [
          test(validAges, "Children's ages must be a valid age"),
          test(
            commaSeparatedDigits,
            "Dependent ages must be comma separated numbers",
          ),
        ]
      : [],
    nearest_relative_name: [
      test(
        lessThanMaxLength(150),
        "Relative name must not exceed 150 characters",
      ),
    ],
    nearest_relative_relationship: [
      test(
        lessThanMaxLength(100),
        "Relationship type must not exceed 100 characters",
      ),
    ],
    nearest_relative_phone_number: formData.nearest_relative_phone_number
      ? [
          test(
            (value: string) =>
              isValidPhoneNumber(stripCountryCode(value || "")),
            "Phone number should be 10 digits",
          ),
        ]
      : [],
  });

export default () => {
  const api = useWebClientApi();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const role = useRoleForBorrower();
  const sectionProgress = useSectionProgress();
  const { formData, updateField } = useFormLoad<PersonalInfoFormData>(
    {},
    () => api.getBorrower(),
    (data) => fromBorrowerPersonalInfo(data as DtoBorrower),
  );

  const hasDependentsInput = Boolean(formData.dependents_ages_years?.trim());

  const validationMapper = useMemo(
    () => getDynamicValidationMapper(hasDependentsInput),
    [hasDependentsInput],
  );
  const formErrors = validate<PersonalInfoFormData>(formData, validationMapper);

  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: PersonalInfoFormData) =>
      api.updateBorrower(toDependentsUpdateReq(data)),
    );
  let nextPagePath = "/address";

  if (role === "COBORROWER") nextPagePath = "/address/coborrower-confirmation";
  if (sectionProgress["personal_info"] && sectionProgress["address"])
    nextPagePath = "../../progress";
  useResponseRedirect(nextPagePath, response);

  const maritalValue = useMemo(
    () => toMaritalStatusFormData(formData),
    [formData],
  );

  const nearestRelativeValue = useMemo(
    () => toNearestRelativeFormData(formData),
    [formData],
  );

  const dependentsErrors = useMemo(
    () =>
      fieldErrorsAfterSubmit(
        {
          dependentsAgesYears: formErrors.dependents_ages_years?.message,
        },
        hasAttemptedFormSubmission,
      ),
    [formErrors.dependents_ages_years, hasAttemptedFormSubmission],
  );

  const nearestRelativeErrors = useMemo(
    () =>
      fieldErrorsAfterSubmit(
        {
          nearestRelativeName: formErrors.nearest_relative_name?.message,
          nearestRelativeRelationship:
            formErrors.nearest_relative_relationship?.message,
          nearestRelativePhone:
            formErrors.nearest_relative_phone_number?.message,
        },
        hasAttemptedFormSubmission,
      ),
    [
      formErrors.nearest_relative_name,
      formErrors.nearest_relative_relationship,
      formErrors.nearest_relative_phone_number,
      hasAttemptedFormSubmission,
    ],
  );

  const onSubmit = () => {
    const dependentsAges =
      parseDependentsAgesString(formData.dependents_ages_years) ??
      maritalValue.dependentsAgesYears;

    const formattedData = {
      ...formData,
      ...fromDependentsFormData(dependentsAges),
      ...formatNearestRelativeForSubmit(nearestRelativeValue),
    };

    void submitHandler(formattedData, formErrors);
  };

  let footerTitle = "";
  if (sectionProgress["personal_info"] && sectionProgress["address"]) {
    footerTitle = "Save";
  } else {
    footerTitle = "Next";
  }

  return (
    <FormLayout
      title={
        <DependentsPageTitle iconClickHandler={() => setIsModalOpen(true)} />
      }
      headerTitle="Personal and Household"
      stepIndex={1}
      footerTitle={footerTitle}
      clickHandler={onSubmit}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Dependents"
          headerIcon={
            <UserGroup02Icon
              variant="bulk-rounded"
              size={16}
              className="text-primary"
            />
          }
        >
          <FormTableBody>
            <MaritalStatusFields
              value={maritalValue}
              showMaritalStatus={false}
              dependentsOnlyLayout
              errors={dependentsErrors}
              onChange={(patch) => {
                const next = {
                  ...maritalValue,
                  ...patch,
                };
                const mapped = fromDependentsFormData(next.dependentsAgesYears);
                for (const [key, val] of Object.entries(mapped)) {
                  if (val !== undefined) {
                    updateField(key as keyof PersonalInfoFormData)(val);
                  }
                }
              }}
            />
          </FormTableBody>
        </FormTable>

        <FormTable
          title="Who is your nearest relative not living with you?"
          headerIcon={
            <UserIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody>
            <NearestRelativeFields
              value={nearestRelativeValue}
              errors={nearestRelativeErrors}
              onChange={(patch) => {
                const next = {
                  ...nearestRelativeValue,
                  ...patch,
                };
                const mapped = fromNearestRelativeFormData(next);
                for (const [key, val] of Object.entries(mapped)) {
                  if (val !== undefined) {
                    updateField(key as keyof PersonalInfoFormData)(val);
                  }
                }
              }}
            />
          </FormTableBody>
        </FormTable>
      </Col>
      <DependentsModal
        isOpen={isModalOpen}
        closeHandler={() => setIsModalOpen(false)}
      />
    </FormLayout>
  );
};
